import { useContext } from "react";
import { ConfiguratorContext, initialContext } from "../context";
import useFinanciadoData from "./useFinanciadoData";

/**
 * Hook central para manejo de datos del configurador
 * Gestiona modelos, versiones, colores, accesorios y planes de mantenimiento
 */
const useConfiguratorData = () => {
    const [state, setState] = useContext(ConfiguratorContext);
    const { resetFinanciado } = useFinanciadoData();

    // Reinicia el contexto con valores iniciales
    const initContext = () => {
        setState((prevState) => ({
            ...prevState,
            accessories: {
                ...initialContext.accessories,
            },
            summary: {
                ...prevState.summary,
                accessories: 0,
            },
        }));
    };

    // === GETTERS ===
    const getModels = () => (state.configurator.models ? state.configurator.models : []);
    const getFilteredModels = () =>
        state.configurator.filteredModels ? state.configurator.filteredModels : [];
    const getSelectedModel = () => (state.selectedModel ? state.selectedModel : null);
    const getSelectedVersion = () => (state.selectedVersion ? state.selectedVersion : null);
    const getColors = () => (state.details.colors ? state.details.colors : []);
    const getSelectedColor = () =>
        state.details.selectedColor ? state.details.selectedColor : null;
    const getFilters = () => (state.configurator.filters ? state.configurator.filters : null);
    const getSummary = () => (state.summary ? state.summary : null);
    const getLoading = () => state.loading;
    const getPrepaidMaintenancePlans = () =>
        state.selectedModel.prepaid_maintenance_plans
            ? state.selectedModel.prepaid_maintenance_plans
            : [];
    const getSelectedPrepaidMaintenancePlan = () => state.selectedPrepaidMaintenancePlan;
    const getPreselectedDealerId = () =>
        state.configurator.preselectedDealerId ? state.configurator.preselectedDealerId : null;
    const getInactiveModalUsed = () => state.inactiveModalUsed;

    // === SETTERS ===
    const setFilteredModels = (filteredModels) => {
        setState((prevState) => ({
            ...prevState,
            configurator: {
                ...prevState.configurator,
                filteredModels,
            },
        }));
    };

    const setInactiveModalUsed = (wasUsed) => {
        setState((prevState) => ({
            ...prevState,
            inactiveModalUsed: wasUsed,
        }));
    };

    const setPreselectedDealerId = (dealerId) => {
        if (!state.configurator.preselectedDealerId) {
            setState((prevState) => ({
                ...prevState,
                configurator: {
                    ...prevState.configurator,
                    preselectedDealerId: dealerId === NaN ? null : dealerId,
                },
            }));
        }
    };

    const setSelectedColor = (color) => {
        setState((prevState) => ({
            ...prevState,
            details: {
                ...prevState.details,
                selectedColor: color,
            },
        }));
    };

    const setSelectedModel = (model) => {
        setState((prevState) => ({
            ...prevState,
            selectedModel: model,
            selectedPrepaidMaintenancePlan: null, // No preseleccionar mantenciones - que el usuario elija
        }));
        resetFinanciado();
    };

    const setSelectedVersion = (version) => {
        setState((prevState) => ({
            ...prevState,
            selectedVersion: version,
            simulationResults: {
                ...initialContext.simulationResults,
            },
            // Force remount of MAF/Kinto simulators to clear local component state.
            simulationKeys: {
                2: prevState.simulationKeys[2] + 1,
                3: prevState.simulationKeys[3] + 1,
            },
            mafSimulationData: {
                ...initialContext.mafSimulationData,
            },
            kintoSimulationData: {
                ...initialContext.kintoSimulationData,
            },
            // Kinto One se reconstruye desde cero con los datos de la versión
            // nueva: si sólo se pisaran car_data/values quedarían vivos
            // `api_response` y las opciones elegidas para la versión anterior.
            kinto_one_personal: {
                ...initialContext.kinto_one_personal,
                enabled: Boolean(version.kinto_car),
                car_data: version.kinto_car || null,
                values: version.kinto_car?.values || [],
            },
            // Estos slices los usa el flujo v1/tradicional y también arrastraban
            // datos de la versión anterior (costo total del crédito, preaprobación)
            simulated_data: {
                ...initialContext.simulated_data,
            },
            preapproval_data: {
                ...initialContext.preapproval_data,
            },
            last_preapproval: {
                ...initialContext.last_preapproval,
            },
            details: {
                ...prevState.details,
                colors: version.colors,
                selectedColor: version.colors[0],
            },
            summary: {
                total_price: version.price,
                accessories: 0,
            },
        }));

        initContext();
        resetFinanciado();
    };

    const setSelectedPrepaidMaintenancePlan = (prepaidMaintenancePlan) => {
        setState((prevState) => ({
            ...prevState,
            selectedPrepaidMaintenancePlan: prepaidMaintenancePlan,
        }));
        resetFinanciado();
    };

    const getVersionAndModelByVersionId = (id) => {
        const result = {
            model: null,
            version: null,
        };
        state.configurator.models.forEach((model) => {
            model.versions.forEach((version) => {
                if (version.id === id) {
                    result.model = model;
                    result.version = version;
                }
            });
        });

        return result;
    };

    //Methods
    const filter = (options) => {
        let filteredModels = state.configurator.models.filter((model) => {
            let segmentMatch, motorMatch, modelMatch, priceMatch;
            segmentMatch = motorMatch = modelMatch = priceMatch = false;

            //Segment
            if (model.segment.id === options.segment.id) segmentMatch = true;
            //Model
            if (model.id === options.model.id) modelMatch = true;
            //Price
            if (
                model.cheapest_price <= options.price.max &&
                model.cheapest_price >= options.price.min
            )
                priceMatch = true;
            //Motor
            model.versions.forEach((version) => {
                if (version.motor) {
                    if (version.motor.id === options.motor.id) motorMatch = true;
                }
            });

            if (options.segment.id === 0) segmentMatch = true;
            if (options.motor.id === 0) motorMatch = true;
            if (options.model.id === 0) modelMatch = true;

            return segmentMatch && motorMatch && modelMatch && priceMatch;
        });

        setFilteredModels(filteredModels);
    };

    return {
        getModels,
        getFilteredModels,
        getSelectedModel,
        getSelectedVersion,
        getColors,
        getSelectedColor,
        getFilters,
        getSummary,
        getLoading,
        getVersionAndModelByVersionId,
        getPrepaidMaintenancePlans,
        getSelectedPrepaidMaintenancePlan,
        getPreselectedDealerId,
        getInactiveModalUsed,
        setPreselectedDealerId,
        setSelectedPrepaidMaintenancePlan,
        setSelectedColor,
        setSelectedModel,
        setSelectedVersion,
        setInactiveModalUsed,
        filter,
    };
};

export default useConfiguratorData;
